"use client";

import { SaveBar } from "@commerce/components/page";
import type { InferRouterOutputs } from "@orpc/server";
import type { ApiRouter } from "@repo/api/orpc/router";
import { orpc, orpcClient } from "@shared/lib/orpc";
import {
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@ui/components/alert-dialog";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import { ButtonGroup } from "@ui/components/button-group";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@ui/components/empty";
import { Input } from "@ui/components/input";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@ui/components/tooltip";
import { cn } from "@ui/lib";
import {
	CircleAlertIcon,
	CircleDotIcon,
	DownloadIcon,
	MapPinIcon,
	MapPinnedIcon,
	PencilIcon,
	PentagonIcon,
	SearchIcon,
	StoreIcon,
	Trash2Icon,
	XIcon,
} from "lucide-react";
import { lazy, Suspense, useMemo, useState } from "react";
import { BranchDetail } from "./BranchDetail";
import type { MapBranch, MapTool } from "./BranchMap";
import {
	areaLabel,
	asZone,
	coverageOf,
	distanceLabel,
	locationQuery,
	overlappingPairs,
	type Position,
	zoneAreaM2,
} from "./geo";

const BranchMap = lazy(() =>
	import("./BranchMap").then((m) => ({ default: m.BranchMap })),
);

type Outputs = InferRouterOutputs<ApiRouter>["stores"];
export type Branch = Outputs["list"]["stores"][number];
export type BranchUpdate = Partial<
	Pick<
		Branch,
		| "latitude"
		| "longitude"
		| "address"
		| "phone"
		| "deliveryZone"
		| "deliveryRadiusM"
		| "hours"
		| "priority"
		| "deliveryEnabled"
		| "pickupEnabled"
		| "fulfilment"
	>
>;
type Resolution = Outputs["checkLocation"];

const route = getRouteApi("/_saas/app/_org/$organizationSlug/branches");

const TOOL_HINT: Record<Exclude<MapTool, null>, string> = {
	zone: "Click to add corners. Click the first corner to finish.",
	circle: "Drag out from the branch to set how far it delivers.",
	edit: "Drag a corner or the whole zone. Click a midpoint to add a corner.",
	pin: "Click where the branch is.",
};

/**
 * Branches and delivery areas (spec 5.7): the map first, with every branch's area side by
 * side so gaps and overlaps show, and the list and the selected branch beside it on lg.
 * Under lg the map sits on top and the side shows the list, then one branch. Edits are kept
 * per branch until saved, so drawing several areas is one save.
 */
export function BranchesScreen({
	organizationId,
	isAdmin,
}: {
	organizationId: string;
	isAdmin: boolean;
}) {
	const search = route.useSearch();
	const navigate = route.useNavigate();
	const { data } = useSuspenseQuery(
		orpc.stores.list.queryOptions({ input: { organizationId } }),
	);

	const [tool, setTool] = useState<MapTool>(null);
	const [checkOpen, setCheckOpen] = useState(false);
	const [checkPoint, setCheckPoint] = useState<Position | null>(null);
	const [importOpen, setImportOpen] = useState(false);

	const selectedId = data.stores.some((s) => s.id === search.id)
		? (search.id ?? null)
		: null;
	const select = (id: string | null) => {
		setTool(null);
		navigate({
			search: (prev) => ({ ...prev, id: id ?? undefined }),
			replace: true,
		});
	};

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<header className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-border/70 border-b px-4 py-3 md:px-6">
				<div className="flex min-w-0 flex-col">
					<h1 className="font-semibold text-xl tracking-tight">
						Branches
					</h1>
					<p className="text-muted-foreground text-sm">
						Where each branch delivers, when it is open and what
						delivery costs.
					</p>
				</div>
				<div className="ml-auto flex flex-wrap items-center gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setCheckOpen(true)}
					>
						<SearchIcon data-icon="inline-start" />
						Check an address
					</Button>
					{isAdmin ? (
						<Button
							variant="outline"
							size="sm"
							onClick={() => setImportOpen(true)}
						>
							<DownloadIcon data-icon="inline-start" />
							Import from connector
						</Button>
					) : null}
				</div>
			</header>

			{data.stores.length === 0 ? (
				<NoBranches />
			) : (
				<BranchesWorkspace
					organizationId={organizationId}
					isAdmin={isAdmin}
					stores={data.stores}
					selectedId={selectedId}
					onSelect={select}
					tool={tool}
					onTool={setTool}
					checkPoint={checkPoint}
				/>
			)}

			<CheckAddressDialog
				open={checkOpen}
				onOpenChange={setCheckOpen}
				organizationId={organizationId}
				onPoint={setCheckPoint}
				onSelect={(id) => {
					select(id);
					setCheckOpen(false);
				}}
			/>
			{isAdmin ? (
				<ImportDialog
					open={importOpen}
					onOpenChange={setImportOpen}
					organizationId={organizationId}
				/>
			) : null}
		</div>
	);
}

function NoBranches() {
	return (
		<Empty className="m-auto">
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<StoreIcon />
				</EmptyMedia>
				<EmptyTitle>No branches yet</EmptyTitle>
				<EmptyDescription>
					Branches come from your catalog source. Run a sync on
					Sources and sync, then set where each one delivers here.
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	);
}

/**
 * Unsaved edits per branch, layered over the saved branches, and the one save that sends
 * them all. Each branch saves on its own: one that fails keeps its draft.
 */
function useBranchDrafts(organizationId: string, stores: readonly Branch[]) {
	const queryClient = useQueryClient();
	const [drafts, setDrafts] = useState<Record<string, BranchUpdate>>({});
	const [saving, setSaving] = useState(false);

	const branches = useMemo(
		() => stores.map((store) => ({ ...store, ...drafts[store.id] })),
		[stores, drafts],
	);
	const dirtyIds = Object.keys(drafts).filter(
		(id) => Object.keys(drafts[id] ?? {}).length > 0,
	);

	const patch = (id: string, change: BranchUpdate) =>
		setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...change } }));
	const discard = (id: string) =>
		setDrafts((prev) => {
			const { [id]: _dropped, ...rest } = prev;
			return rest;
		});
	const discardAll = () => setDrafts({});

	/** Saves one branch; what went wrong, in words, or null when it saved. */
	const saveOne = async (
		id: string,
		change: BranchUpdate,
	): Promise<string | null> => {
		try {
			const saved = await orpcClient.stores.update({
				organizationId,
				storeId: id,
				...change,
			});
			queryClient.setQueryData(
				orpc.stores.list.queryKey({ input: { organizationId } }),
				(old) =>
					old
						? {
								stores: old.stores.map((s) =>
									s.id === id ? saved : s,
								),
							}
						: old,
			);
			discard(id);
			return null;
		} catch (error) {
			const name = stores.find((s) => s.id === id)?.name ?? "A branch";
			return `${name}: ${error instanceof Error ? error.message : "not saved"}`;
		}
	};

	const save = async () => {
		setSaving(true);
		try {
			// Branches are independent rows, so they save side by side.
			const outcomes = await Promise.all(
				dirtyIds.flatMap((id) => {
					const change = drafts[id];
					return change ? [saveOne(id, change)] : [];
				}),
			);
			const failed = outcomes.filter((outcome) => outcome !== null);
			if (failed.length > 0) {
				toast.add({
					type: "error",
					title:
						failed.length === 1
							? "One branch wasn't saved"
							: `${failed.length} branches weren't saved`,
					description: failed.join(". "),
				});
			} else {
				toast.add({
					type: "success",
					title:
						dirtyIds.length === 1
							? "Branch saved"
							: `${dirtyIds.length} branches saved`,
				});
			}
		} finally {
			setSaving(false);
		}
	};

	return { branches, dirtyIds, patch, discard, discardAll, save, saving };
}

/** Why a map tool can't be used on the selected branch, or null when it can. */
function toolDisabledReason(
	selected: Branch | null,
	which: Exclude<MapTool, null>,
): string | null {
	if (!selected) {
		return "Select a branch first";
	}
	if (
		which === "circle" &&
		(selected.latitude === null || selected.longitude === null)
	) {
		return "Set the branch's location first";
	}
	if (which === "edit" && asZone(selected.deliveryZone)?.type !== "Polygon") {
		return "Draw a zone first";
	}
	return null;
}

function BranchesWorkspace({
	organizationId,
	isAdmin,
	stores,
	selectedId,
	onSelect,
	tool,
	onTool,
	checkPoint,
}: {
	organizationId: string;
	isAdmin: boolean;
	stores: readonly Branch[];
	selectedId: string | null;
	onSelect: (id: string | null) => void;
	tool: MapTool;
	onTool: (tool: MapTool) => void;
	checkPoint: Position | null;
}) {
	const defaults = useQuery({
		...orpc.orders.settings.get.queryOptions({ input: { organizationId } }),
		enabled: isAdmin,
	});
	const { branches, dirtyIds, patch, discard, discardAll, save, saving } =
		useBranchDrafts(organizationId, stores);

	const coverage = useMemo(
		() =>
			branches.map((branch) => ({
				id: branch.id,
				coverage: coverageOf(branch),
			})),
		[branches],
	);
	const overlaps = useMemo(() => {
		const byId = new Map<string, string[]>();
		for (const [a, b] of overlappingPairs(coverage)) {
			byId.set(a, [...(byId.get(a) ?? []), b]);
			byId.set(b, [...(byId.get(b) ?? []), a]);
		}
		return byId;
	}, [coverage]);
	const mapBranches: MapBranch[] = useMemo(
		() =>
			branches.map((branch, index) => ({
				id: branch.id,
				name: branch.name,
				position:
					branch.latitude !== null && branch.longitude !== null
						? [branch.longitude, branch.latitude]
						: null,
				coverage: coverage[index]?.coverage ?? null,
				overlaps: overlaps.has(branch.id),
			})),
		[branches, coverage, overlaps],
	);

	const selected = branches.find((b) => b.id === selectedId) ?? null;
	const savedById = new Map(stores.map((s) => [s.id, s]));
	const saveBar = (
		<SaveBar
			visible={dirtyIds.length > 0}
			message={
				dirtyIds.length === 1
					? "Unsaved changes to 1 branch"
					: `Unsaved changes to ${dirtyIds.length} branches`
			}
			saving={saving}
			onSave={save}
			onDiscard={discardAll}
		/>
	);

	return (
		<div className="flex min-h-0 flex-1 flex-col lg:flex-row">
			<div className="relative h-64 shrink-0 sm:h-80 lg:h-auto lg:min-w-0 lg:flex-1">
				<BranchMapPane
					isAdmin={isAdmin}
					branches={mapBranches}
					selected={selected}
					onSelect={onSelect}
					tool={tool}
					onTool={onTool}
					onPatch={patch}
					checkPoint={checkPoint}
				/>
			</div>

			<aside
				aria-label="Branches"
				className="flex min-h-0 min-w-0 flex-1 flex-col border-border/70 max-lg:border-t lg:w-[26rem] lg:flex-none lg:border-l xl:w-[30rem]"
			>
				<BranchList
					branches={branches}
					selectedId={selected?.id ?? null}
					overlaps={overlaps}
					dirtyIds={dirtyIds}
					onSelect={onSelect}
				/>
				{selected ? (
					<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
						<BranchDetail
							key={selected.id}
							branch={selected}
							saved={savedById.get(selected.id) ?? selected}
							isAdmin={isAdmin}
							overlapsWith={(overlaps.get(selected.id) ?? []).map(
								(id) => savedById.get(id)?.name ?? id,
							)}
							defaults={defaults.data?.deliveryDefaults ?? null}
							onChange={(change) => patch(selected.id, change)}
							onDiscard={() => discard(selected.id)}
							onBack={() => onSelect(null)}
							onTool={onTool}
						/>
						<div className="px-4 pb-4">{saveBar}</div>
					</div>
				) : dirtyIds.length > 0 ? (
					<div className="px-4 pb-4">{saveBar}</div>
				) : null}
			</aside>
		</div>
	);
}

/** The map, and over it the drawing tools for the selected branch (admins only). */
function BranchMapPane({
	isAdmin,
	branches,
	selected,
	onSelect,
	tool,
	onTool,
	onPatch,
	checkPoint,
}: {
	isAdmin: boolean;
	branches: MapBranch[];
	selected: Branch | null;
	onSelect: (id: string) => void;
	tool: MapTool;
	onTool: (tool: MapTool) => void;
	onPatch: (id: string, change: BranchUpdate) => void;
	checkPoint: Position | null;
}) {
	const selectedZone = selected ? asZone(selected.deliveryZone) : null;
	const patchSelected = (change: BranchUpdate) => {
		if (selected) {
			onPatch(selected.id, change);
		}
	};
	return (
		<>
			<Suspense
				fallback={
					<Skeleton className="absolute inset-0 rounded-none" />
				}
			>
				<BranchMap
					className="absolute inset-0"
					branches={branches}
					selectedId={selected?.id ?? null}
					onSelect={onSelect}
					tool={tool}
					editZone={
						selectedZone?.type === "Polygon" ? selectedZone : null
					}
					onZoneChange={(zone) =>
						patchSelected({
							deliveryZone: zone,
							deliveryRadiusM: null,
						})
					}
					onCircle={(_center, radiusM) => {
						if (selected) {
							onPatch(selected.id, {
								deliveryZone: null,
								deliveryRadiusM: Math.min(
									100_000,
									Math.max(100, radiusM),
								),
							});
							toast.add({
								type: "info",
								title: `Delivers ${distanceLabel(radiusM)} around ${selected.name}`,
								description:
									"The circle is centred on the branch's location.",
							});
						}
					}}
					onPin={([lng, lat]) =>
						patchSelected({ latitude: lat, longitude: lng })
					}
					onToolDone={() => onTool(null)}
					checkPoint={checkPoint}
				/>
			</Suspense>
			{isAdmin ? (
				// Top left, under nothing: the attribution and zoom keep their corners.
				<div className="pointer-events-none absolute top-3 right-14 left-3 z-10 flex flex-col items-start gap-2">
					<MapToolbar
						tool={tool}
						onTool={onTool}
						disabledReason={(which) =>
							toolDisabledReason(selected, which)
						}
						canRemove={Boolean(
							selected &&
								(selected.deliveryZone ||
									selected.deliveryRadiusM),
						)}
						onRemove={() => {
							if (selected) {
								onTool(null);
								onPatch(selected.id, {
									deliveryZone: null,
									deliveryRadiusM: null,
								});
							}
						}}
					/>
					{tool ? (
						<p className="pointer-events-auto flex max-w-full items-center gap-2 rounded-2xl bg-popover py-1.5 pr-1.5 pl-3 text-popover-foreground text-sm shadow-(--shadow-overlay) ring-1 ring-foreground/8">
							{TOOL_HINT[tool]}
							<Button
								variant="ghost"
								size="icon-xs"
								aria-label="Stop drawing"
								onClick={() => onTool(null)}
							>
								<XIcon />
							</Button>
						</p>
					) : null}
				</div>
			) : null}
		</>
	);
}

function BranchList({
	branches,
	selectedId,
	overlaps,
	dirtyIds,
	onSelect,
}: {
	branches: readonly Branch[];
	selectedId: string | null;
	overlaps: ReadonlyMap<string, string[]>;
	dirtyIds: readonly string[];
	onSelect: (id: string) => void;
}) {
	const dirty = new Set(dirtyIds);
	const uncovered = branches.filter(
		(b) => b.deliveryEnabled && !coverageOf(b),
	).length;
	return (
		<div
			className={cn(
				"flex min-h-0 flex-col",
				selectedId
					? "max-lg:hidden lg:max-h-[36%] lg:shrink-0 lg:border-border/70 lg:border-b"
					: "flex-1",
			)}
		>
			<div className="flex flex-wrap items-center gap-2 px-4 pt-3 pb-2">
				<span className="font-medium text-sm">
					{branches.length.toLocaleString("en-US")}{" "}
					{branches.length === 1 ? "branch" : "branches"}
				</span>
				{uncovered > 0 ? (
					<Badge variant="warning">
						{uncovered} without a delivery area
					</Badge>
				) : null}
				{overlaps.size > 0 ? (
					<Badge variant="warning">
						{overlaps.size} with overlapping areas
					</Badge>
				) : null}
			</div>
			<ul className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-2">
				{branches.map((branch) => (
					<li key={branch.id}>
						<BranchRow
							branch={branch}
							selected={branch.id === selectedId}
							overlapping={overlaps.has(branch.id)}
							unsaved={dirty.has(branch.id)}
							onSelect={() => onSelect(branch.id)}
						/>
					</li>
				))}
			</ul>
		</div>
	);
}

/** What a branch's delivery area is, in a few words, or null when it has none. */
function coverageLabel(branch: Branch): string | null {
	const zone = asZone(branch.deliveryZone);
	if (zone) {
		return `Zone · ${areaLabel(zoneAreaM2(zone))}`;
	}
	return branch.deliveryRadiusM
		? `Radius ${distanceLabel(branch.deliveryRadiusM)}`
		: null;
}

function BranchRow({
	branch,
	selected,
	overlapping,
	unsaved,
	onSelect,
}: {
	branch: Branch;
	selected: boolean;
	overlapping: boolean;
	unsaved: boolean;
	onSelect: () => void;
}) {
	const coverage = coverageLabel(branch);
	const ways = [
		branch.deliveryEnabled ? "Delivery" : null,
		branch.pickupEnabled ? "Pickup" : null,
	].filter(Boolean);
	return (
		<button
			type="button"
			onClick={onSelect}
			aria-current={selected ? "true" : undefined}
			className={cn(
				"flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left outline-none transition-colors duration-(--duration-hover) focus-visible:ring-3 focus-visible:ring-ring/30",
				selected ? "bg-muted" : "hover:bg-muted/60",
			)}
		>
			<span
				aria-hidden
				className={cn(
					"size-2 shrink-0 rounded-full",
					selected
						? "bg-brand"
						: coverage
							? "bg-foreground/40"
							: "bg-transparent ring-1 ring-foreground/30",
				)}
			/>
			<span className="flex min-w-0 flex-1 flex-col">
				<span className="flex items-center gap-2">
					<span className="truncate font-medium text-sm" dir="auto">
						{branch.name}
					</span>
					{branch.isDefault ? (
						<Badge variant="secondary">Default</Badge>
					) : null}
					{unsaved ? <Badge variant="brand">Unsaved</Badge> : null}
				</span>
				<span className="truncate text-muted-foreground text-xs">
					{ways.length > 0 ? ways.join(" and ") : "Not taking orders"}
				</span>
			</span>
			<span className="flex shrink-0 items-center gap-1.5 text-muted-foreground text-xs">
				<BranchCoverage
					missing={branch.deliveryEnabled && !coverageOf(branch)}
					coverage={coverage}
				/>
				{overlapping ? <OverlapMark /> : null}
			</span>
		</button>
	);
}

function BranchCoverage({
	missing,
	coverage,
}: {
	missing: boolean;
	coverage: string | null;
}) {
	if (missing) {
		return (
			<span className="flex items-center gap-1 text-warning-foreground dark:text-warning">
				<CircleAlertIcon className="size-3.5" aria-hidden />
				No area
			</span>
		);
	}
	return coverage ? (
		<span className="tabular">{coverage}</span>
	) : (
		<span>Pickup only</span>
	);
}

function OverlapMark() {
	return (
		<Tooltip>
			<TooltipTrigger
				render={
					<span className="flex text-warning-foreground dark:text-warning" />
				}
			>
				<PentagonIcon
					className="size-3.5"
					aria-label="Overlaps another branch"
				/>
			</TooltipTrigger>
			<TooltipContent>Overlaps another branch's area</TooltipContent>
		</Tooltip>
	);
}

function MapToolbar({
	tool,
	onTool,
	disabledReason,
	canRemove,
	onRemove,
}: {
	tool: MapTool;
	onTool: (tool: MapTool) => void;
	disabledReason: (tool: Exclude<MapTool, null>) => string | null;
	canRemove: boolean;
	onRemove: () => void;
}) {
	const tools = [
		{ id: "zone", label: "Draw zone", icon: PentagonIcon },
		{ id: "circle", label: "Circle", icon: CircleDotIcon },
		{ id: "edit", label: "Edit", icon: PencilIcon },
		{ id: "pin", label: "Set location", icon: MapPinIcon },
	] as const;
	return (
		<div className="pointer-events-auto max-w-full overflow-x-auto rounded-2xl bg-popover p-1 shadow-(--shadow-overlay) ring-1 ring-foreground/8">
			<ButtonGroup aria-label="Map tools">
				{tools.map(({ id, label, icon: Icon }) => {
					const reason = disabledReason(id);
					const button = (
						<Button
							variant={tool === id ? "secondary" : "ghost"}
							size="sm"
							aria-pressed={tool === id}
							disabled={Boolean(reason)}
							onClick={() => onTool(tool === id ? null : id)}
						>
							<Icon data-icon="inline-start" />
							<span className="max-sm:sr-only">{label}</span>
						</Button>
					);
					return (
						<Tooltip key={id}>
							<TooltipTrigger
								render={<span className="inline-flex" />}
							>
								{button}
							</TooltipTrigger>
							<TooltipContent>{reason ?? label}</TooltipContent>
						</Tooltip>
					);
				})}
				<Tooltip>
					<TooltipTrigger render={<span className="inline-flex" />}>
						<Button
							variant="ghost"
							size="sm"
							disabled={!canRemove}
							onClick={onRemove}
						>
							<Trash2Icon data-icon="inline-start" />
							<span className="max-sm:sr-only">Delete</span>
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						{canRemove
							? "Remove the delivery area"
							: "No area to remove"}
					</TooltipContent>
				</Tooltip>
			</ButtonGroup>
		</div>
	);
}

const RULE_LABEL: Record<Resolution["rule"], string> = {
	adapter: "your ordering system chose it",
	zone: "inside its drawn zone",
	radius: "within its delivery radius",
	area: "matched by the area's name",
	nearest: "the nearest branch",
};

function CheckAddressDialog({
	open,
	onOpenChange,
	organizationId,
	onPoint,
	onSelect,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	organizationId: string;
	onPoint: (point: Position | null) => void;
	onSelect: (storeId: string) => void;
}) {
	const [text, setText] = useState("");
	const check = useMutation(orpc.stores.checkLocation.mutationOptions());
	const query = locationQuery(text);
	const submit = () => {
		if (!query) {
			return;
		}
		onPoint("lat" in query ? [query.lng, query.lat] : null);
		check.mutate({ organizationId, where: query });
	};
	const result = check.data;
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Check an address</DialogTitle>
					<DialogDescription>
						See which branch a shopper there is matched to, the way
						the agent does it.
					</DialogDescription>
				</DialogHeader>
				<form
					className="flex gap-2"
					onSubmit={(event) => {
						event.preventDefault();
						submit();
					}}
				>
					<Input
						value={text}
						onChange={(e) => setText(e.target.value)}
						placeholder="Coordinates, a Google Maps link or an area"
						aria-label="Address to check"
						dir="auto"
						autoFocus
					/>
					<Button type="submit" disabled={!query || check.isPending}>
						{check.isPending ? (
							<Spinner data-icon="inline-start" />
						) : null}
						Check
					</Button>
				</form>
				{check.isError ? (
					<p className="text-destructive text-sm">
						{check.error instanceof Error
							? check.error.message
							: "Couldn't check this address. Try again."}
					</p>
				) : null}
				{result ? (
					<CheckResult result={result} onSelect={onSelect} />
				) : null}
			</DialogContent>
		</Dialog>
	);
}

function CheckResult({
	result,
	onSelect,
}: {
	result: Resolution;
	onSelect: (storeId: string) => void;
}) {
	const { store, nearestPickup } = result;
	return (
		<div className="flex flex-col gap-3 rounded-2xl bg-muted/60 p-4 text-sm">
			{store ? (
				<div className="flex flex-wrap items-center gap-2">
					<MapPinnedIcon className="size-4" aria-hidden />
					<span>
						<span className="font-medium">{store.name}</span>{" "}
						<span className="text-muted-foreground">
							({RULE_LABEL[result.rule]})
						</span>
					</span>
					<Button
						variant="outline"
						size="xs"
						className="ml-auto"
						onClick={() => onSelect(store.id)}
					>
						Open branch
					</Button>
				</div>
			) : (
				<div className="flex items-center gap-2">
					<CircleAlertIcon
						className="size-4 text-warning-foreground dark:text-warning"
						aria-hidden
					/>
					<span className="font-medium">
						{noBranchMessage(result.reason)}
					</span>
				</div>
			)}
			{result.pointless ? (
				<p className="text-muted-foreground">
					Matched from the area's name only. That picks the prices a
					shopper sees; delivery still needs a pin or a Maps link.
				</p>
			) : null}
			{result.candidates.length > 1 ? (
				<NearbyBranches
					candidates={result.candidates.filter(
						(c) => c.id !== store?.id,
					)}
				/>
			) : null}
			{!store && nearestPickup ? (
				<p className="text-muted-foreground">
					Nearest pickup: {nearestPickup.name},{" "}
					{distanceLabel(nearestPickup.distanceM)} away.
				</p>
			) : null}
		</div>
	);
}

function noBranchMessage(reason: Resolution["reason"]): string {
	if (reason === "closed") {
		return "The branch for this address is closed now";
	}
	if (reason === "ambiguous") {
		return "More than one branch could serve this address";
	}
	return "No branch delivers here";
}

function NearbyBranches({
	candidates,
}: {
	candidates: Resolution["candidates"];
}) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-muted-foreground text-xs">
				Other branches nearby
			</span>
			<ul className="flex flex-col gap-0.5">
				{candidates.slice(0, 4).map((candidate) => (
					<li
						key={candidate.id}
						className="flex justify-between gap-3"
					>
						<span dir="auto">{candidate.name}</span>
						{candidate.distanceM !== null ? (
							<span className="text-muted-foreground tabular">
								{distanceLabel(candidate.distanceM)}
							</span>
						) : null}
					</li>
				))}
			</ul>
		</div>
	);
}

function ImportDialog({
	open,
	onOpenChange,
	organizationId,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	organizationId: string;
}) {
	const queryClient = useQueryClient();
	const importGeo = useMutation({
		...orpc.stores.importGeo.mutationOptions(),
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: orpc.stores.key() });
			onOpenChange(false);
			toast.add({
				type: "success",
				title:
					result.updated === 1
						? "1 branch updated from your connector"
						: `${result.updated} branches updated from your connector`,
				description:
					result.skipped > 0
						? `${result.skipped} set here were kept as they are.`
						: undefined,
			});
		},
		onError: (error) =>
			toast.add({
				type: "error",
				title: "Couldn't import locations",
				description: error instanceof Error ? error.message : undefined,
			}),
	});
	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>
						Import locations from your connector?
					</AlertDialogTitle>
					<AlertDialogDescription>
						Reads each branch's location, address and delivery zone
						from your catalog source. Branches whose area was set
						here keep it.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel>Cancel</AlertDialogCancel>
					<AlertDialogAction
						disabled={importGeo.isPending}
						onClick={(event) => {
							event.preventDefault();
							importGeo.mutate({ organizationId });
						}}
					>
						{importGeo.isPending ? (
							<Spinner data-icon="inline-start" />
						) : null}
						Import
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}

export function BranchesSkeleton() {
	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<div className="flex items-center justify-between gap-3 border-border/70 border-b px-4 py-3 md:px-6">
				<div className="flex flex-col gap-2">
					<Skeleton className="h-7 w-28" />
					<Skeleton className="h-4 w-72 max-sm:w-48" />
				</div>
				<Skeleton className="h-8 w-36 max-sm:hidden" />
			</div>
			<div className="flex min-h-0 flex-1 flex-col lg:flex-row">
				<Skeleton className="h-64 shrink-0 rounded-none sm:h-80 lg:h-auto lg:flex-1" />
				<div className="flex flex-col gap-1 p-2 lg:w-[26rem] xl:w-[30rem]">
					{Array.from({ length: 7 }).map((_, i) => (
						<div
							key={i}
							className="flex items-center gap-3 px-3 py-2.5"
						>
							<Skeleton className="size-2 rounded-full" />
							<div className="flex flex-1 flex-col gap-1.5">
								<Skeleton className="h-4 w-1/2" />
								<Skeleton className="h-3 w-1/3" />
							</div>
							<Skeleton className="h-3 w-16" />
						</div>
					))}
				</div>
			</div>
		</div>
	);
}
